/**
 * PrintBlueprint — dedikált, chrome-mentes nyomtatási route.
 *
 * Az URL-séma: `/print/blueprint/:type?id=<entry_id>`
 *   • :type ∈ {client, offer, voice}
 *   • ?id=  — opcionális; ha jelen van, a Tudástár (saved_blueprints)
 *     adott bejegyzését hozza; ha hiányzik, a user aktív Foundation
 *     blueprint-jét.
 *
 * OLDALSORREND (típusonként, mindegyik saját oldalon, krém alapon
 * éltől élig):
 *   • Avatar:    [Cover] → [1-oldalas Stratégiai Snapshot] → [Riport] → [Impresszum]
 *   • Márkahang: [Cover] → [Riport] → [Impresszum]
 *   • Ajánlat:   [Cover] → [Riport] → [Impresszum]
 *
 * Single source of truth: a tényleges UI riport HTML (renderAvatarBlueprintV3
 * / renderOfferBlueprintV3 / renderVoiceBlueprintV2). A Cover / Snapshot /
 * Impresszum csak a PDF körüli design-keret — NEM jelenik meg az app-beli
 * riportokban.
 *
 * "Render kész" jel: amint a blueprint + (Avatar esetén a Snapshot) +
 * user.name betöltődik és a DOM commit lezajlik, beállítja
 * `window.__PRINT_READY__ = true`-t. A backend `page.wait_for_function`
 * figyel erre, és csak utána hívja `page.pdf()`-et.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { RENDERERS } from "../components/blueprintRenderers";

// ── Akcent színek típusonként (a renderer-ekből származtatva, NEM
//    duplikálva — egyetlen helyen, hogy ha változik a Foundation lila,
//    csak itt kell módosítani). ────────────────────────────────────────
const ACCENT_BY_TYPE = {
  client: "#5e50f7",  // Foundation lila — Avatar
  voice: "#5e50f7",   // Foundation lila — Márkahang
  offer: "#7c6cf2",   // V2_PURPLE — renderOfferBlueprintV3-ban használt accent
};

// ── Krém oldal-háttér (a riport krém #f7f3eb / #f6f1e7 család egyik
//    enyhébb árnyalata, hogy éltől élig egységes legyen). ─────────────
const CREAM_PAGE_BG = "#f4f1ea";

// ── Riport-cím típusonként. ──────────────────────────────────────────
const REPORT_TITLE = {
  client: "Avatar Stratégiai Blueprint",
  voice: "Márkahang Stratégiai Blueprint",
  offer: "Ajánlat Stratégiai Blueprint",
};

const REPORT_EYEBROW = {
  client: "FOUNDATION · 01 · AVATAR",
  voice: "FOUNDATION · 02 · MÁRKAHANG",
  offer: "FOUNDATION · 03 · AJÁNLAT",
};


/**
 * Magyar névsorrend: Google profil `name` mező általában "Keresztnév
 * Vezetéknév" (nyugati) sorrendet ad. Ha 2 részből áll, megfordítjuk
 * vezetéknév-keresztnév sorrendre. Egy szóból álló név változatlan.
 */
function toHungarianName(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 2) {
    return `${parts[1]} ${parts[0]}`;
  }
  return trimmed;
}


/* ════════════════════════════════════════════════════════════════════
 * COVER OLDAL
 * Designos, krém alapon, akcent színnel, középre rendezett. A riport
 * címe, "Készítette:" + user neve magyar sorrendben, diszkrét MEX Magic
 * jellel az oldal alján.
 * ════════════════════════════════════════════════════════════════════ */
function CoverPage({ type, userName, accent }) {
  return (
    <section className="pdf-page pdf-page--cover" data-print-page="cover">
      <div className="pdf-cover-inner">
        {/* Tetején diszkrét accent szalag */}
        <div
          style={{
            height: 4,
            width: 56,
            background: accent,
            borderRadius: 2,
            marginBottom: 60,
          }}
        />

        {/* Eyebrow */}
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: accent,
            marginBottom: 28,
          }}
        >
          {REPORT_EYEBROW[type] || "FOUNDATION"}
        </div>

        {/* Riport címe */}
        <h1
          style={{
            fontSize: 56,
            fontWeight: 600,
            color: "#15141c",
            letterSpacing: "-0.022em",
            lineHeight: 1.05,
            margin: 0,
            marginBottom: 96,
            maxWidth: "92%",
          }}
        >
          {REPORT_TITLE[type] || "Stratégiai Blueprint"}
        </h1>

        {/* "Készítette:" sor — diszkrét label + nagy, hangsúlyos név.
            A blueprintek (Avatar / Márkahang / Ajánlat) BELSŐ stratégiai
            anyagok a coachnak — "Készítette: MEX Magic" (NEM a user).
            Az ügyfélnek menő Mikromágnes PDF más renderer-en megy és
            ott marad a user neve magyar sorrendben. */}
        <div style={{ marginTop: "auto" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#7d7a8c",
              marginBottom: 12,
            }}
          >
            Készítette
          </div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 500,
              color: "#1c1a2b",
              letterSpacing: "-0.012em",
              lineHeight: 1.2,
              marginBottom: 36,
            }}
          >
            MEX Magic
          </div>

          {/* Diszkrét accent jel */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              opacity: 0.72,
            }}
          >
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: accent,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.20em",
                textTransform: "uppercase",
                color: "#3a384a",
              }}
            >
              Belső stratégiai blueprint
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════════════════
 * AVATAR STRATÉGIAI SNAPSHOT (1 oldal, CSAK Avatar-on)
 * Szellős 6 szekciós executive overview a Snapshot endpoint adataiból.
 * ════════════════════════════════════════════════════════════════════ */
function AvatarSnapshotPage({ snapshot, accent }) {
  if (!snapshot) return null;

  const Eyebrow = ({ children }) => (
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: accent,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );

  const Body = ({ children, style }) => (
    <div
      style={{
        fontSize: 12.5,
        color: "#1c1a2b",
        lineHeight: 1.55,
        ...style,
      }}
    >
      {children}
    </div>
  );

  const list = (arr) =>
    Array.isArray(arr) ? arr.filter((x) => x && String(x).trim()) : [];

  const fajList = list(snapshot.ami_leginkabb_faj);
  const vagyList = list(snapshot.amire_igazan_vagyik);
  const beszedList = list(snapshot.igy_kell_hozza_beszelni);

  return (
    <section className="pdf-page pdf-page--snapshot" data-print-page="snapshot">
      <div className="pdf-snapshot-inner">
        {/* Title strip */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: accent,
              marginBottom: 8,
            }}
          >
            Stratégiai Snapshot
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 600,
              color: "#15141c",
              letterSpacing: "-0.014em",
              lineHeight: 1.18,
            }}
          >
            Az Avatar 1 oldalas executive overview-ja
          </div>
        </div>

        {/* 2-oszlopos grid (6 szekció) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 32,
            rowGap: 22,
          }}
        >
          {/* 1. KI EZ AZ AVATAR */}
          <div style={{ gridColumn: "1 / span 2" }}>
            <Eyebrow>Ki ez az avatar</Eyebrow>
            <Body>{snapshot.ki_ez_az_avatar || ""}</Body>
          </div>

          {/* 2. FŐ BELSŐ FESZÜLTSÉG */}
          <div style={{ gridColumn: "1 / span 2" }}>
            <Eyebrow>Fő belső feszültség</Eyebrow>
            <Body>{snapshot.fo_belso_feszultseg || ""}</Body>
          </div>

          {/* 3. AMI LEGINKÁBB FÁJ */}
          <div>
            <Eyebrow>Ami leginkább fáj</Eyebrow>
            <ul style={{ margin: 0, paddingLeft: 16, color: "#1c1a2b" }}>
              {fajList.map((x, i) => (
                <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 6 }}>
                  {x}
                </li>
              ))}
            </ul>
          </div>

          {/* 4. AMIRE IGAZÁN VÁGYIK */}
          <div>
            <Eyebrow>Amire igazán vágyik</Eyebrow>
            <ul style={{ margin: 0, paddingLeft: 16, color: "#1c1a2b" }}>
              {vagyList.map((x, i) => (
                <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 6 }}>
                  {x}
                </li>
              ))}
            </ul>
          </div>

          {/* 5. ÍGY KELL HOZZÁ BESZÉLNI */}
          <div style={{ gridColumn: "1 / span 2" }}>
            <Eyebrow>Így kell hozzá beszélni</Eyebrow>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {beszedList.map((x, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 11.5,
                    padding: "5px 11px",
                    borderRadius: 999,
                    background: "rgba(94,80,247,0.10)",
                    border: `1px solid ${accent}33`,
                    color: "#1c1a2b",
                  }}
                >
                  {x}
                </span>
              ))}
            </div>
          </div>

          {/* 6. LEGFONTOSABB KOMMUNIKÁCIÓS IRÁNY */}
          <div style={{ gridColumn: "1 / span 2", marginTop: 6 }}>
            <Eyebrow>Legfontosabb kommunikációs irány</Eyebrow>
            <Body style={{ fontStyle: "italic", color: "#3a384a" }}>
              {snapshot.legfontosabb_kommunikacios_irany || ""}
            </Body>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════════════════
 * IMPRESSZUM — utolsó oldal, "Made with love by MEX Magic"
 * ════════════════════════════════════════════════════════════════════ */
function ImprintPage({ accent }) {
  return (
    <section className="pdf-page pdf-page--imprint" data-print-page="imprint">
      <div className="pdf-imprint-inner">
        <div
          style={{
            height: 3,
            width: 40,
            background: accent,
            borderRadius: 2,
            marginBottom: 28,
          }}
        />

        <h2
          style={{
            fontSize: 18,
            fontWeight: 500,
            color: "#3a384a",
            letterSpacing: "-0.008em",
            lineHeight: 1.3,
            margin: 0,
            marginBottom: 12,
            maxWidth: "75%",
          }}
        >
          Made with love by <strong style={{ fontWeight: 700, color: "#15141c" }}>MEX Magic</strong>.
        </h2>

        <p
          style={{
            fontSize: 10.5,
            color: "#6a687a",
            lineHeight: 1.6,
            margin: 0,
            maxWidth: "75%",
          }}
        >
          Belső stratégiai blueprint a coach felhasználásra; az aktív Avatar,
          Márkahang és Ajánlat állapotát tükrözi a generálás pillanatában.
        </p>

        {/* Lábsor — MEX Magic jel */}
        <div
          style={{
            marginTop: "auto",
            paddingTop: 20,
            display: "flex",
            alignItems: "center",
            gap: 8,
            opacity: 0.55,
          }}
        >
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: accent,
            }}
          />
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 600,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#3a384a",
            }}
          >
            MEX Magic
          </span>
        </div>
      </div>
    </section>
  );
}


/* ════════════════════════════════════════════════════════════════════
 * FŐKOMPONENS
 * ════════════════════════════════════════════════════════════════════ */
export default function PrintBlueprint() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const entryId = searchParams.get("id");
  // Az appban NYITOTT V2Collapsible szekciók ID-jei (vesszővel
  // szétválasztva). A render után pontosan ezeket nyitjuk ki — a
  // többit hagyjuk csukva. Így a PDF az UI-állapotot tükrözi.
  const openIdsParam = searchParams.get("open") || "";
  const openIds = openIdsParam ? openIdsParam.split(",").filter(Boolean) : [];

  const [blueprint, setBlueprint] = useState(null);
  const [snapshot, setSnapshot] = useState(null);          // CSAK Avatar
  const [snapshotNeeded] = useState(type === "client");
  const [snapshotResolved, setSnapshotResolved] = useState(type !== "client");
  const [userName, setUserName] = useState("");
  const [userResolved, setUserResolved] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const accent = ACCENT_BY_TYPE[type] || "#5e50f7";

  // ── Body/HTML fehér helyett krém legyen a print-route-on (a print.css
  //    `body.print-route`-szelektorok bekapcsolása). ─────────────────
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__PRINT_READY__ = false;
    }
    const prev = {
      bodyBg: document.body.style.background,
      htmlBg: document.documentElement.style.background,
      bodyColor: document.body.style.color,
    };
    document.body.classList.add("print-route");
    document.body.style.background = CREAM_PAGE_BG;
    document.documentElement.style.background = CREAM_PAGE_BG;
    document.body.style.color = "#15141c";
    return () => {
      document.body.classList.remove("print-route");
      document.body.style.background = prev.bodyBg;
      document.documentElement.style.background = prev.htmlBg;
      document.body.style.color = prev.bodyColor;
    };
  }, []);

  // ── Blueprint + user.name betöltése. ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // user.name a Cover + Imprint-hez (magyar sorrendre fordítva).
        const meRes = await api.get("/auth/me");
        if (!cancelled) {
          setUserName(toHungarianName(meRes.data?.name || ""));
          setUserResolved(true);
        }
      } catch {
        if (!cancelled) {
          setUserName("");
          setUserResolved(true);
        }
      }

      try {
        let bp = null;
        if (entryId) {
          const res = await api.get(`/asset-vault/${entryId}`);
          bp = res.data?.blueprint || null;
        } else {
          const endpoint =
            type === "voice" ? "/foundation/brand-voice" :
            type === "client" ? "/foundation/avatar" :
            type === "offer" ? "/foundation/offer" : null;
          if (!endpoint) throw new Error(`Ismeretlen típus: ${type}`);
          const res = await api.get(endpoint);
          bp = res.data?.blueprint || null;
        }
        if (cancelled) return;
        if (!bp) {
          setError("Nincs elmentett blueprint.");
          setLoading(false);
          setSnapshotResolved(true);
          return;
        }
        setBlueprint(bp);
        setLoading(false);

        // ── Avatar Snapshot fetch (1-oldalas Stratégiai overview).
        if (type === "client") {
          try {
            const sres = await api.post("/foundation/avatar/pdf/prepare", {
              blueprint: bp,
            });
            if (!cancelled) {
              setSnapshot(sres.data?.snapshot || {});
              setSnapshotResolved(true);
            }
          } catch (e) {
            // Csendes fallback — üres snapshot, az oldal nem renderelődik.
            if (!cancelled) {
              setSnapshot(null);
              setSnapshotResolved(true);
            }
          }
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || "Betöltési hiba");
        setLoading(false);
        setSnapshotResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [type, entryId]);

  // ── Render-ready jel: minden függőség (blueprint + snapshot[Avatar] +
  //    user) letöltött + DOM commit. Itt csomagoljuk wrapper-be a
  //    keretes dobozokat (article-k) is, hogy a krém levegő a border
  //    FÖLÖTT legyen, NE a border-en belül. ─────────────────────────
  useEffect(() => {
    if (!blueprint) return;
    if (!userResolved) return;
    if (snapshotNeeded && !snapshotResolved) return;
    let rafIds = [];
    rafIds.push(requestAnimationFrame(() => {
      // ── 1) Nyitott V2Collapsible szekciók kinyitása az UI-állapot
      //    szerint. Az `openIds` listában szereplő `data-testid`-jű
      //    button-ok megkapnak egy programatikus `click()`-et, ami a
      //    V2Collapsible saját `setOpen(true)`-jét triggereli. Csak a
      //    csukottakat nyitjuk, hogy elkerüljük a duplikációt.
      try {
        if (openIds.length > 0) {
          openIds.forEach((id) => {
            const btn = document.querySelector(`button[data-testid="${id}"]`);
            if (!btn) return;
            // A V2Collapsible button-on a `▾` ikon transform alapján
            // állapítjuk meg az aktuális open-állapotot.
            const arrow = btn.querySelector('span[style*="rotate"]');
            const isOpen = arrow && (arrow.style.transform || "").includes("180");
            if (!isOpen) {
              btn.click();
            }
          });
        }
      } catch (e) {
        console.warn("[Print] Open-collapsible error:", e);
      }
      // ── 2) Keretes dobozok (article-k) wrapelése .pdf-block-wrap-be,
      //    hogy a krém padding-top a border FÖLÖTT legyen. ─────────────
      // Egy rövid timeout, hogy az 1) collapsible-nyitás után a DOM
      // reflowoljon (a kinyitott szekciókban új <article>-ek jelennek
      // meg, ezeket is wrappelnünk kell).
      const wrapAndSignal = () => {
        try {
          const root = document.querySelector('[data-testid="blueprint-output"]');
          if (root) {
            const selectors = ['article'].join(",");
            root.querySelectorAll(selectors).forEach((el) => {
              if (el.parentElement && el.parentElement.classList.contains("pdf-block-wrap")) return;
              const wrap = document.createElement("div");
              wrap.className = "pdf-block-wrap";
              el.parentElement.insertBefore(wrap, el);
              wrap.appendChild(el);
            });
          }
        } catch (e) {
          console.warn("[Print] DOM wrap error:", e);
        }
        rafIds.push(requestAnimationFrame(() => {
          const fire = () => {
            window.__PRINT_READY__ = true;
            window.dispatchEvent(new Event("blueprint-print-ready"));
          };
          if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === "function") {
            document.fonts.ready.then(fire);
          } else {
            fire();
          }
        }));
      };

      // Ha vannak nyitandó collapsible-ek, várjunk 1 RAF-et hogy az
      // állapotváltozás flush-oljon és a `{open && ...}` content
      // mountoljon, mielőtt wrappelünk és jelzünk.
      if (openIds.length > 0) {
        rafIds.push(requestAnimationFrame(() => {
          rafIds.push(requestAnimationFrame(wrapAndSignal));
        }));
      } else {
        wrapAndSignal();
      }
    }));
    return () => { rafIds.forEach((id) => cancelAnimationFrame(id)); };
  }, [blueprint, userResolved, snapshotNeeded, snapshotResolved]);

  if (loading) {
    return (
      <div className="print-page-root" data-testid="print-loading" style={{ padding: 40, fontFamily: "Inter, sans-serif" }}>
        Betöltés…
      </div>
    );
  }
  if (error) {
    return (
      <div className="print-page-root" data-testid="print-error" style={{ padding: 40, fontFamily: "Inter, sans-serif", color: "#a55" }}>
        Hiba: {error}
      </div>
    );
  }

  const renderer = RENDERERS[type];
  if (!renderer) {
    return (
      <div className="print-page-root" data-testid="print-unknown-type" style={{ padding: 40 }}>
        Ismeretlen típus: {type}
      </div>
    );
  }

  return (
    <div className="print-page-root">
      {/* COVER — mindhárom típusnál */}
      <CoverPage type={type} userName={userName} accent={accent} />

      {/* AVATAR STRATÉGIAI SNAPSHOT — csak Avatar-on */}
      {type === "client" && snapshot && Object.keys(snapshot).length > 0 && (
        <AvatarSnapshotPage snapshot={snapshot} accent={accent} />
      )}

      {/* TÉNYLEGES UI RIPORT — egyetlen rendering forrás */}
      <section className="pdf-page pdf-page--report" data-print-page="report">
        <div data-testid="blueprint-output">
          {renderer(blueprint)}
        </div>
      </section>

      {/* IMPRESSZUM — mindhárom típusnál, saját oldalon */}
      <ImprintPage accent={accent} />
    </div>
  );
}
